import { Component, EventEmitter, Injector, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AbstractComponent, Attendance, AttendanceWhisper, AttendanceWhisperService, WHISPER_MAX_LENGTH } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { getWhispers } from '../../state/actions';
import { AppState } from '../../state/app.state';

// The whisper's own field (SPEC-2026-09-attendance-whisper, RF-02): separate from the customer
// composer, so what is typed here can only ever become a whisper.
@Component({
    selector: 'attendance-whisper-field-component',
    templateUrl: './attendance-whisper-field.component.html',
    styleUrls: ['./attendance-whisper-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendanceWhisperFieldComponent extends AbstractComponent implements OnChanges {

  @Input() attendance: Attendance;
  @Input() replyTo: AttendanceWhisper | null = null;
  @Input() editing: AttendanceWhisper | null = null;
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  readonly maxLength = WHISPER_MAX_LENGTH;
  content = new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(WHISPER_MAX_LENGTH)] });
  sending = false;
  // Kept across retries of the same text so the API can refuse a duplicate (RNF-05).
  private clientId: string | null = null;

  constructor(
    injector: Injector,
    private whisperService: AttendanceWhisperService,
    private store: Store<AppState>,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editing']) {
      this.content.setValue(this.editing?.content ?? '');
    }
    if (changes['attendance'] && !changes['attendance'].firstChange) {
      this.content.setValue('');
      this.clientId = null;
    }
  }

  get canSend(): boolean {
    return !this.sending && this.content.valid && this.content.value.trim().length > 0;
  }

  onEnter(event: Event): void {
    if ((event as KeyboardEvent).shiftKey) {
      return;
    }
    event.preventDefault();
    this.send();
  }

  send(): void {
    if (!this.canSend) {
      return;
    }
    const text = this.content.value.trim();
    this.sending = true;
    if (!this.editing && !this.clientId) {
      this.clientId = uuidv4();
    }
    const request$ = this.editing
      ? this.whisperService.update(this.editing._id, <AttendanceWhisper>{ content: text })
      : this.whisperService.send({ attendance: this.attendance._id, content: text, replyTo: this.replyTo?._id, clientId: this.clientId });

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.afterSent(),
      error: (err) => {
        this.sending = false;
        // The first attempt went through and this is its repeat: refresh instead of complaining.
        if (err?.status === 409 && err?.error?.key === 'errors.attendanceWhisper.alreadySent') {
          this.afterSent();
          return;
        }
        this.alertService.warning(this.t(this.editing ? 'attendance.whisper.editFailed' : 'attendance.whisper.notSent'));
      },
    });
  }

  cancel(): void {
    this.content.setValue('');
    this.clientId = null;
    this.closed.emit();
  }

  private afterSent(): void {
    const wasReplyOrEdit = !!this.replyTo || !!this.editing;
    this.sending = false;
    this.clientId = null;
    this.content.setValue('');
    this.store.dispatch(getWhispers({ idAttendance: this.attendance._id }));
    if (wasReplyOrEdit) {
      this.closed.emit();
    }
  }
}
